"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Active, Cancelled" },
  { from: "Active", to: "Paused, Cancelled" },
  { from: "Paused", to: "Active, Cancelled" },
  {
    from: "Completed",
    to: "Cancelled on the list only. You cannot Mark As Completed yourself — the job does that when the last cycle is done",
  },
  {
    from: "Cancelled",
    to: "Nothing. Mark Recurring Invoice As is hidden. Cancelled is a dead end",
  },
];

const RULES = [
  {
    topic: "This is a schedule, not an invoice",
    rule: "Saving a Recurring Invoice does not create an Invoice, deduct stock, post the ledger, or change the customer's Current Balance. Those happen later, when a real Invoice is generated",
  },
  {
    topic: "Subscription gate",
    rule: "Creating a Recurring Invoice is limited by your plan. Editing, Mark As, and deleting are not. Retrying a failed cycle (Create Invoice on Recurring Activities) also checks Create and the plan feature",
  },
  {
    topic: "Save always makes Draft",
    rule: "There is no Save & Active. Nothing generates until you Mark As Active. Draft and Paused are skipped by the daily job",
  },
  {
    topic: "Recurring Invoice number",
    rule: "Auto-generated unless you turn that off under Recurring Invoice Configuration on the form (prefix, next number, Terms & Conditions). Default prefix is RECI. Locked after save",
  },
  {
    topic: "Division, Branch, Warehouse, Customer",
    rule: "All four are required to create, plus Profile Name, Repeat Every, Frequency, Start On, Payment Terms, shipping address, and at least one item. After save, location and customer stay locked. You can still change items, amounts, schedule, and address on a Draft",
  },
  {
    topic: "Currency",
    rule: "Taken from the customer at creation. An Exchange Rate field appears when that currency differs from the company base",
  },
  {
    topic: "No primary-bank gate on this module",
    rule: "Unlike Quote, Sales Order, Delivery Challan, and Invoice, you can save a Recurring Invoice without a primary bank. When an Invoice is generated, that invoice copies the company's primary bank if one exists",
  },
  {
    topic: "Stock and ledger live on the generated Invoice",
    rule: "Settings → Inventory Config for Invoice applies when the Invoice is created (stock check / stock deduct). The Recurring Invoice itself never takes warehouse quantity. Ledger posting follows the Invoice rules once that Draft invoice exists",
  },
  {
    topic: "You can only edit or delete a Draft",
    rule: "Active, Paused, Completed, and Cancelled cannot be edited or deleted. Cancelled invoices already generated from this schedule are not deleted with it",
  },
  {
    topic: "Paused does not catch up",
    rule: "While Paused, due dates are skipped. Marking Active again does not create the missed invoices. Only the next date that still matches the original Start On + Repeat Every pattern will generate",
  },
  {
    topic: "Generated invoices start as Draft",
    rule: "The job creates a Draft Invoice (INV prefix from Invoice Configuration), copies items, charges, notes, and payment terms, and sets due date from those terms on the generation date. You still Mark Invoice As Sent on the Invoices module. Current Balance is updated when the invoice is generated, including while it is Draft",
  },
  {
    topic: "Failed cycle retry",
    rule: "If generation fails (for example stock check), Recurring Activities shows FAILED with the error. Create Invoice on that row makes a Draft invoice from the current template and marks that cycle SUCCESS. Only a FAILED history row can be retried",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a recurring invoice" },
      { heading: "2. Repeat schedule" },
      { heading: "3. How invoices actually get created" },
      { heading: "4. Viewing, searching, and the list toolbar" },
      { heading: "5. Editing a recurring invoice" },
      { heading: "6. Changing status" },
      { heading: "7. Recurring Activities — success, failed, retry" },
      { heading: "8. The invoice this schedule creates" },
      { heading: "9. Deleting a recurring invoice" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function RecurringInvoicesPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Recurring Invoices
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone who bills the same customer, same items, on a fixed
            repeat — weekly retainers, monthly rent, yearly AMC
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Recurring Invoice</B> is a saved template plus a schedule. You
            fill it once, <B>Mark As Active</B>, and Books creates a real{" "}
            <B>Invoice</B> on each due date. Those invoices land as{" "}
            <B>Draft</B> in the Invoices module — this page does not collect
            payment, apply credit, or generate e-invoices.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a recurring invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Add opens a sales form. Pick <B>Division</B>, then <B>Branch</B>,
            then <B>Warehouse</B> — the rest stays locked until those three
            are set. There is only <B>Save</B>. That creates a{" "}
            <B>Draft</B>. It does not start the schedule.
          </p>
          <Definitions
            items={[
              {
                term: "Profile Name",
                desc: (
                  <>
                    Required. This is the name of the schedule, not the invoice
                    number.
                  </>
                ),
              },
              {
                term: "Invoice# (Recurring Invoice number)",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Recurring Invoice Configuration</B> on the form (prefix
                    and next number). That same drawer stores{" "}
                    <B>Terms & Conditions</B>. Default prefix is <B>RECI</B>.
                    Locked after save.
                  </>
                ),
              },
              {
                term: "Customer and shipping address",
                desc: (
                  <>
                    Customer must belong to the selected branch. The address
                    has to be one of that customer&apos;s own addresses. If
                    they are not on this branch, the form shows they are not
                    available here.
                  </>
                ),
              },
              {
                term: "Payment Terms",
                desc: (
                  <>
                    Required. Copied from the customer when you pick them, or{" "}
                    <B>Due On Receipt</B> if the customer has none. Used later
                    to calculate the generated invoice&apos;s due date — there
                    is no due-date field on this form.
                  </>
                ),
              },
              {
                term: "Price List",
                desc: (
                  <>
                    Optional. Same customer price-list behaviour as other sales
                    documents, including Include Price List Item Only when that
                    is on for the customer.
                  </>
                ),
              },
              {
                term: "Currency and exchange rate",
                desc: (
                  <>
                    Currency comes from the customer. If it differs from the
                    company base (<B>Settings → Currencies</B>), an{" "}
                    <B>Exchange Rate</B> field appears.
                  </>
                ),
              },
              {
                term: "Items, discounts, charges, and tax",
                desc: (
                  <>
                    Line items with quantity and rate. Document discount
                    follows <B>Settings → Discounts</B> — Enable Discount, then
                    Discount Applies On <B>Item Level</B> or{" "}
                    <B>Transaction Level</B>. Extra lines such as freight come
                    from <B>Settings → Additional charges</B> on this module.
                    GST (CGST / SGST / IGST) from customer state vs company
                    state. Round-off is on the totals.
                  </>
                ),
              },
              {
                term: "Reference, Subject, Customer Notes, Terms",
                desc: (
                  <>
                    Optional. Terms pre-fill from Recurring Invoice
                    Configuration. All of these copy onto each generated
                    Invoice.
                  </>
                ),
              },
              {
                term: "What this Save does not do",
                desc: (
                  <>
                    No Invoice is created. No stock check or deduct. No ledger.
                    No Current Balance change. No e-invoice. There is no
                    Duplicate and no Convert from Quote / Sales Order /
                    Delivery Challan.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Add Recurring Invoice with Profile Name, Repeat Every, Frequency, Start On, Never Expires, and Save only." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Repeat schedule
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Repeat Every plus Frequency is how often an invoice is due. Start
            On cannot be before today. End On must be after Start On, unless{" "}
            <B>Never Expires</B> is ticked — then End On is not required and
            is disabled.
          </p>
          <Definitions
            items={[
              {
                term: "Repeat Every",
                desc: (
                  <>
                    <B>Day(s)</B>, <B>Week(s)</B>, <B>Month(s)</B>, or{" "}
                    <B>Year(s)</B>.
                  </>
                ),
              },
              {
                term: "Frequency",
                desc: (
                  <>
                    How many of that period. 1 Month(s) is monthly. 2 Week(s)
                    is every two weeks.
                  </>
                ),
              },
              {
                term: "When the first invoice actually falls",
                desc: (
                  <>
                    For <B>Day(s)</B>, Start On itself is a generation day if
                    the profile is already Active. For Week(s), Month(s), and
                    Year(s), the first generation is Start On{" "}
                    <B>plus</B> that frequency — for example Start On 1 Jan,
                    Repeat Every 1 Month(s) → first invoice 1 Feb.
                  </>
                ),
              },
              {
                term: "Never Expires",
                desc: (
                  <>
                    Keeps generating on the pattern with no End On. The profile
                    does not move to Completed on its own.
                  </>
                ),
              },
              {
                term: "End On",
                desc: (
                  <>
                    After a successful generation, if the next due date would
                    fall on or after End On, the profile is marked{" "}
                    <B>Completed</B> and stops.
                  </>
                ),
              },
              {
                term: "Next Execution Date / Last Invoice Date",
                desc: (
                  <>
                    Shown on the list and the detail. Last Invoice Date is when
                    an invoice was last generated. Next Execution Date is the
                    next due date from the same pattern.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            Changing Start On, End On, or Repeat Every only sticks if the
            profile is still Draft. Once it is Active you cannot edit the
            schedule — Pause does not reopen the form.
          </Callout>
          <Screenshot caption="Screenshot: Repeat Every Month(s), Frequency 1, Start On, End On, and Never Expires." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. How invoices actually get created
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            A daily job looks at every <B>Active</B> Recurring Invoice whose
            End On is still in the future, or Never Expires. If today matches
            the Start On + Repeat Every pattern, and this date has not already
            succeeded, it creates one Invoice.
          </p>
          <Definitions
            items={[
              {
                term: "Must be Active",
                desc: (
                  <>
                    Draft, Paused, Completed, and Cancelled are not picked up.
                    Mark As Active is on the list row and the detail header.
                  </>
                ),
              },
              {
                term: "One successful invoice per due date",
                desc: (
                  <>
                    If today already has a SUCCESS row in Recurring Activities,
                    that date is skipped.
                  </>
                ),
              },
              {
                term: "If generation fails",
                desc: (
                  <>
                    Recurring Activities gets a <B>FAILED</B> row with the
                    error (stock check is a common one). The profile stays
                    Active. See section 7 to retry that cycle.
                  </>
                ),
              },
              {
                term: "Paused skips dates",
                desc: (
                  <>
                    Dates that fell while Paused are not created later. Going
                    back to Active only generates the next date that still
                    matches the original pattern.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            The Recurring Invoice is not itself an Invoice. Payment Received,
            Apply Credit, Dispatch, and E-Invoice all sit on the generated
            invoice in the Invoices module — after you send that Draft.
          </Callout>
          <Screenshot caption="Screenshot: Mark Recurring Invoice As → Active on a Draft, with Next Execution Date on the header." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Viewing, searching, and the list toolbar
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The list and the detail page cover different needs. This list has
            no Export and no Auto Map.
          </p>
          <Definitions
            items={[
              {
                term: "Finding a profile",
                desc: (
                  <>
                    Search by Recurring Invoice number, subject, reference, or
                    customer name. Profile Name is not searched. Filters: date
                    (the profile&apos;s Recurring Invoice Date), and status
                    (Draft, Active, Paused, Completed, Cancelled).
                  </>
                ),
              },
              {
                term: "List columns",
                desc: (
                  <>
                    Recurring Invoice#, Customer, Status, Profile Name,
                    Frequency, Last Invoice Date, Next Invoice Date, amounts,
                    Division, Branch, Warehouse, Added By, Updated By. Manage
                    Custom Fields, Refresh List, and Reset Columns Width sit
                    under the toolbar.
                  </>
                ),
              },
              {
                term: "Preferences",
                desc: (
                  <>
                    Same Recurring Invoice Configuration as on Add: prefix,
                    next number, Terms & Conditions.
                  </>
                ),
              },
              {
                term: "Row actions",
                desc: (
                  <>
                    View, Edit (Draft only), Delete (Draft only), and Mark
                    Recurring Invoice As.
                  </>
                ),
              },
              {
                term: "Detail header",
                desc: (
                  <>
                    Number and status. <B>View Customer</B>, <B>Download</B>,
                    and <B>Print</B>.
                  </>
                ),
              },
              {
                term: "Detail tabs",
                desc: (
                  <>
                    <B>Basic Info</B> — profile, schedule, items, totals,
                    notes. <B>Next Invoice</B> — a preview of the invoice
                    layout using Next Execution Date (no invoice number yet).{" "}
                    <B>History</B> — comments. <B>Recurring Activities</B> —
                    every generation cycle. Attachments only if the template
                    has files.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Recurring Invoices list with status, Last Invoice Date, Next Invoice Date, and Mark As on the row." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Editing a recurring invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only a <B>Draft</B> can be edited. The backend rejects any other
            status.
          </p>
          <Definitions
            items={[
              {
                term: "What stays locked even on a Draft",
                desc: (
                  <>
                    Division, Branch, Warehouse, Customer, and the Recurring
                    Invoice number.
                  </>
                ),
              },
              {
                term: "What you can still change",
                desc: (
                  <>
                    Profile Name, schedule, payment terms, items, rates,
                    discounts, charges, notes, terms, price list, shipping
                    address, and exchange rate.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Edit on a Draft Recurring Invoice with Division, Branch, Warehouse, and Customer disabled." />

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Changing status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <B>Mark Recurring Invoice As</B> is on the list row and the detail
            header. Cancelled asks for confirm. Completed is not a Mark As
            option — the job sets it.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    You can Mark As
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((row, i) => (
                  <tr
                    key={row.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[180px]">
                      {row.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {row.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Definitions
            items={[
              {
                term: "Active",
                desc: (
                  <>
                    The daily job can generate invoices. This is the only
                    status that does.
                  </>
                ),
              },
              {
                term: "Paused",
                desc: (
                  <>
                    Stops generation without cancelling. Resume with Mark As
                    Active. Missed dates are not backfilled.
                  </>
                ),
              },
              {
                term: "Completed",
                desc: (
                  <>
                    Set automatically after the last cycle when End On is
                    reached. Cannot go back to Active or Paused. The list still
                    offers Cancelled; the detail hides Mark As.
                  </>
                ),
              },
              {
                term: "Cancelled",
                desc: (
                  <>
                    Stops the schedule. Invoices already generated stay. Their
                    comment history is marked cancelled for this source. You
                    cannot bring the Recurring Invoice back.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Mark Recurring Invoice As on an Active profile offering Paused and Cancelled." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Recurring Activities — success, failed, retry
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            This tab is the execution log. Each row is one cycle: Cycle No,
            Scheduled Date, Execution Date, Next Execution Date, the generated
            Invoice number (when it succeeded), and status.
          </p>
          <Definitions
            items={[
              {
                term: "SUCCESS",
                desc: (
                  <>
                    The Invoice number is a link to that invoice. No retry
                    action.
                  </>
                ),
              },
              {
                term: "FAILED",
                desc: (
                  <>
                    View Error shows the failure reason and the item lines that
                    were being billed. <B>Create Invoice</B> retries that cycle
                    only — it is blocked unless the row is FAILED.
                  </>
                ),
              },
              {
                term: "Create Invoice (retry)",
                desc: (
                  <>
                    Builds a Draft Invoice from the current template, same as
                    the job would: Invoice number from Invoice Configuration,
                    due date from payment terms, stock check / deduct from{" "}
                    <B>Settings → Inventory Config</B> for Invoice, Current
                    Balance updated. Then that history row becomes SUCCESS.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Recurring Activities with a FAILED cycle, View Error, and Create Invoice." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. The invoice this schedule creates
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            After generation you work in <B>Invoices</B>, not here. The new
            document is a normal Invoice linked back to this Recurring Invoice.
          </p>
          <Definitions
            items={[
              {
                term: "Status",
                desc: (
                  <>
                    Always <B>Draft</B> at create. Mark Invoice As Sent
                    yourself. Payments, credit notes, and Apply Credit are the
                    Invoice module&apos;s functions.
                  </>
                ),
              },
              {
                term: "What is copied",
                desc: (
                  <>
                    Customer, branch, warehouse, items, amounts, additional
                    charges, discount, notes, terms, price list, shipping
                    address, currency, and attachments from the template.
                    Invoice Date is the generation date. Due Date is calculated
                    from Payment Terms on that date.
                  </>
                ),
              },
              {
                term: "Stock",
                desc: (
                  <>
                    If Stock Deduct Source is Invoice, warehouse quantity is
                    taken when this Draft is generated — same as saving a
                    scratch invoice. Stock Check Source = Invoice can fail the
                    cycle instead.
                  </>
                ),
              },
              {
                term: "Customer Current Balance",
                desc: (
                  <>
                    Goes up by the invoice total when the invoice is generated,
                    including while it is still Draft.
                  </>
                ),
              },
              {
                term: "What is not on this Recurring Invoice",
                desc: (
                  <>
                    No Payment Received, no Apply Credit, no Dispatch, no
                    E-Invoice / E-Way Bill, no Convert.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: a Draft invoice opened from Recurring Activities, Converted From / comment showing the Recurring Invoice number." />

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Deleting a recurring invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Delete is Draft only — same as edit. The API rejects any other
            status.
          </p>
          <Definitions
            items={[
              {
                term: "What delete does not do",
                desc: (
                  <>
                    It does not remove invoices already generated. Those stay
                    in Invoices. Their comment history is marked deleted for
                    this source.
                  </>
                ),
              },
              {
                term: "Use Cancel instead",
                desc: (
                  <>
                    Once the profile has been Active, you cannot delete it.
                    Mark As Cancelled to stop further invoices.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Delete disabled on an Active Recurring Invoice with the status tooltip." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/sales/invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where each generated Draft invoice is sent, paid, and credited
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/customer-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Customer <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Payment terms, price list, and Current Balance this schedule uses
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/payments-received"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Payments Received <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              How a generated invoice becomes Partially Paid or Paid
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
